import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { faceService } from '../services/faceService'

const Faces = () => {
  const { user } = useAuth()
  const [faces, setFaces] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('register')
  const [uploadForm, setUploadForm] = useState({
    personName: '',
    relationship: 'other',
    email: '',
    phone: '',
    notes: ''
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)

  useEffect(() => {
    loadFaces()
  }, [])

  const loadFaces = async () => {
    try {
      setLoading(true)
      console.log('🔍 Loading faces...')
      const response = await faceService.getRegisteredFaces()
      console.log('📋 Full API response:', response)
      
      // The API returns {success: true, data: [...], count: N}
      const facesData = response.data || []
      console.log('👥 Faces data:', facesData)
      console.log('📊 Number of faces:', facesData.length)
      
      if (facesData.length > 0) {
        console.log('🔍 First face structure:', facesData[0])
        console.log('🖼️ First face images:', facesData[0]?.images)
      }
      
      setFaces(facesData)
    } catch (error) {
      console.error('❌ Faces loading error:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error('Failed to load registered faces')
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file)
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
      } else {
        toast.error('Please select an image file')
      }
    }
  }

  const handleInputChange = (e) => {
    setUploadForm({
      ...uploadForm,
      [e.target.name]: e.target.value
    })
  }

  const handleRegisterFace = async (e) => {
    e.preventDefault()
    
    if (!selectedFile) {
      toast.error('Please select an image file')
      return
    }

    if (!uploadForm.personName.trim()) {
      toast.error('Please enter a person name')
      return
    }

    setUploading(true)
    try {
      const result = await faceService.registerFace(
        selectedFile,
        uploadForm.personName,
        uploadForm.relationship,
        uploadForm.email,
        uploadForm.phone,
        uploadForm.notes
      )

      toast.success('Face registered successfully!')
      setFaces(prev => [result.data, ...prev])
      
      // Reset form
      setUploadForm({
        personName: '',
        relationship: 'other',
        email: '',
        phone: '',
        notes: ''
      })
      setSelectedFile(null)
      setPreviewUrl(null)
      setActiveTab('gallery')
      
    } catch (error) {
      toast.error(error.message || 'Failed to register face')
    } finally {
      setUploading(false)
    }
  }

  const relationships = [
    { value: 'family', label: 'Family' },
    { value: 'friend', label: 'Friend' },
    { value: 'colleague', label: 'Colleague' },
    { value: 'acquaintance', label: 'Acquaintance' },
    { value: 'other', label: 'Other' }
  ]

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Face Recognition 👤
              </h1>
              <p className="text-gray-600 mt-1">
                Register faces for AI-powered recognition and manage your contacts
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Registered Faces: {faces.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('register')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'register'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Register Face
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gallery'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Face Gallery ({faces.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'register' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Register New Face
                </h2>
                
                <form onSubmit={handleRegisterFace} className="space-y-6">
                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photo
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors">
                      <div className="space-y-2 text-center">
                        {previewUrl ? (
                          <div className="mb-4">
                            <img
                              src={previewUrl}
                              alt="Preview"
                              className="mx-auto h-32 w-32 object-cover rounded-lg"
                            />
                          </div>
                        ) : (
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        <div className="flex text-sm text-gray-600">
                          <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500">
                            <span>Upload a photo</span>
                            <input
                              type="file"
                              className="sr-only"
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </div>
                    </div>
                  </div>

                  {/* Person Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Person Name *
                      </label>
                      <input
                        type="text"
                        name="personName"
                        value={uploadForm.personName}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="Enter person's name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Relationship
                      </label>
                      <select
                        name="relationship"
                        value={uploadForm.relationship}
                        onChange={handleInputChange}
                        className="input-field"
                      >
                        {relationships.map(rel => (
                          <option key={rel.value} value={rel.value}>
                            {rel.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email (Optional)
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={uploadForm.email}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="person@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={uploadForm.phone}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={uploadForm.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="input-field"
                      placeholder="Any additional notes about this person..."
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={uploading || !selectedFile}
                      className="btn-primary flex items-center space-x-2"
                    >
                      {uploading && <LoadingSpinner size="small" />}
                      <span>{uploading ? 'Registering...' : 'Register Face'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="large" />
                </div>
              ) : faces.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👤</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No faces registered yet
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Register your first face to start using AI-powered recognition
                  </p>
                  <button
                    onClick={() => setActiveTab('register')}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    Register Face
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {faces.map((face) => (
                    <div key={face._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Image */}
                      <div className="aspect-square bg-gray-200">
                        <img
                          src={(() => {
                            const imageUrl = face.images?.[0]?.url;
                            console.log('Face image URL for', face.personName, ':', imageUrl);
                            if (imageUrl) {
                              return imageUrl.startsWith('/') ? `http://localhost:5000${imageUrl}` : imageUrl;
                            }
                            return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjEwMCIgY3k9IjgwIiByPSIzMCIgZmlsbD0iIzlCOUJBMCIvPgo8cGF0aCBkPSJNNTAgMTUwQzUwIDEyNS4xNDcgNzEuMTQ3IDEwNSA5NyAxMDVIMTAzQzEyOC44NTMgMTA1IDE1MCAyNS4xNDcgMTUwIDE1MEg1MFoiIGZpbGw9IiM5QjlCQTAiLz4KPC9zdmc+';
                          })()}
                          alt={face.personName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Silently set fallback image
                            e.target.onerror = null; // Prevent infinite loop
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjEwMCIgY3k9IjgwIiByPSIzMCIgZmlsbD0iIzlCOUJBMCIvPgo8cGF0aCBkPSJNNTAgMTUwQzUwIDEyNS4xNDcgNzEuMTQ3IDEwNSA5NyAxMDVIMTAzQzEyOC44NTMgMTA1IDE1MCAyNS4xNDcgMTUwIDE1MEg1MFoiIGZpbGw9IiM5QjlCQTAiLz4KPC9zdmc+';
                          }}
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {face.personName}
                        </h3>
                        
                        <div className="flex items-center mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 capitalize">
                            {face.relationship}
                          </span>
                          {face.confidence && (
                            <span className="ml-2 text-xs text-gray-600">
                              {Math.round(face.confidence)}% confidence
                            </span>
                          )}
                        </div>
                        
                        {face.contactInfo?.email && (
                          <p className="text-sm text-gray-600 mb-1">
                            📧 {face.contactInfo.email}
                          </p>
                        )}
                        
                        {face.contactInfo?.phone && (
                          <p className="text-sm text-gray-600 mb-1">
                            📱 {face.contactInfo.phone}
                          </p>
                        )}
                        
                        {face.analysis?.facialFeatures && (
                          <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                            {face.analysis.facialFeatures}
                          </p>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          Registered {formatDate(face.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Faces
